import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api, { formatApiError } from "../../lib/api";
import { CURRENCIES, money } from "../../lib/format";
import { Receipt, PiggyBank, ShieldCheck, Zap, ShoppingCart } from "lucide-react";

const ALLOC = [
  { key: "bills", label: "Bills", icon: Receipt, color: "#EA580C" },
  { key: "household", label: "Household", icon: ShoppingCart, color: "#65A30D" },
  { key: "savings", label: "Savings", icon: PiggyBank, color: "#0D9488" },
  { key: "emergency", label: "Emergency", icon: ShieldCheck, color: "#0284C7" },
  { key: "unplanned", label: "Unplanned", icon: Zap, color: "#D97706" },
];

export default function SettingsDialog({ open, onOpenChange, user, onSaved, readOnly }) {
  const s = user?.settings || {};
  const [name, setName] = useState(user?.name || "");
  const [currency, setCurrency] = useState(s.currency || "USD");
  const [income, setIncome] = useState(String(s.monthly_income_target || ""));
  const [cap, setCap] = useState(String(s.monthly_spending_cap || ""));
  const [alloc, setAlloc] = useState({
    bills: String(s.allocations?.bills ?? ""),
    household: String(s.allocations?.household ?? ""),
    savings: String(s.allocations?.savings ?? ""),
    emergency: String(s.allocations?.emergency ?? ""),
    unplanned: String(s.allocations?.unplanned ?? ""),
  });
  const [saving, setSaving] = useState(false);

  const num = (v) => Number(v || 0);
  const myWallet = num(income) - ALLOC.reduce((a, x) => a + num(alloc[x.key]), 0);

  const save = async () => {
    if (readOnly) return toast.error("View-only account — editing is disabled.");
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        name,
        currency,
        monthly_income_target: num(income),
        monthly_spending_cap: num(cap),
        allocations: {
          bills: num(alloc.bills), household: num(alloc.household), savings: num(alloc.savings),
          emergency: num(alloc.emergency), unplanned: num(alloc.unplanned),
        },
      });
      onSaved(data);
      toast.success("Budget settings updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="font-display">Budget Settings</DialogTitle>
          <DialogDescription>Re-plan your monthly income, wallet allocations and spending cap.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="settings-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Income target</Label>
              <Input data-testid="settings-income-input" type="number" className="font-mono-nums" value={income} onChange={(e) => setIncome(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Spending cap</Label>
              <Input data-testid="settings-cap-input" type="number" className="font-mono-nums" value={cap} onChange={(e) => setCap(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Wallet allocations</Label>
            {ALLOC.map((a) => (
              <div key={a.key} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${a.color}1a`, color: a.color }}><a.icon className="w-4 h-4" /></div>
                <span className="text-sm text-slate-600 flex-1">{a.label}</span>
                <Input data-testid={`settings-alloc-${a.key}`} type="number" className="w-32 font-mono-nums" value={alloc[a.key]} onChange={(e) => setAlloc({ ...alloc, [a.key]: e.target.value })} />
              </div>
            ))}
          </div>

          <div className={`flex justify-between p-3 rounded-xl text-sm font-medium ${myWallet < 0 ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
            <span>My Wallet (free money)</span>
            <span className="font-mono-nums font-bold">{money(myWallet, currency)}</span>
          </div>

          <Button data-testid="settings-save-button" onClick={save} disabled={saving || readOnly} className="w-full bg-orange-600 hover:bg-orange-700 text-white">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
